import React, { ChangeEvent, ReactNode, RefObject } from "react";
import { HiPlus } from "react-icons/hi2";

import Button from "../../../components/button/Button";
import { CgAttachment } from "react-icons/cg";
import classNames from "classnames";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

type BaseProps = {
  setError?: (error: string) => void;
  inputRef: RefObject<HTMLInputElement>;
  isLoading?: boolean;
  isEmailPage?: boolean;
  accept?: string;
  button?: ReactNode;
  isWorkPage?: boolean;
  setIsOpenErrorPopUp: React.Dispatch<React.SetStateAction<boolean>>;
};

type SingleFileProps = {
  multiple?: false;
  setFile: (file: File) => void;
};

type MultipleFileProps = {
  multiple: true;
  setFile: (files: File[]) => void;
};

type Props = BaseProps & (SingleFileProps | MultipleFileProps);
// Email attachments stay at 5 MB; client and work files allow 50 MB (same as the server).
const EMAIL_MAX_FILE_SIZE = 5 * 1024 * 1024;
const FILE_MAX_FILE_SIZE = 50 * 1024 * 1024;
export const InputFile: React.FC<Props> = ({
  setFile,
  setError,
  inputRef,
  isLoading,
  isEmailPage,
  accept,
  button,
  isWorkPage,
  multiple,
  setIsOpenErrorPopUp,
}) => {
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const filesArray = Array.from(event.target.files);

    const maxFileSize = isEmailPage ? EMAIL_MAX_FILE_SIZE : FILE_MAX_FILE_SIZE;
    const oversizedFile = filesArray.find((file) => file.size > maxFileSize);
    if (oversizedFile) {
      setIsOpenErrorPopUp(true);
      event.target.value = "";
      return;
    }

    if (multiple === true) {
      setFile(filesArray);
    } else {
      setFile(filesArray[0]);
    }

    setError?.("");
    event.target.value = "";
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={
          accept ||
          ".pdf,.xls,.xlsx,.doc,.docx,image/*,.ppt,.pptx,application/msword,application/vnd.ms-excel,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-powerpoint.presentation"
        }
        className="absolute left-[-9999px] top-[-9999px]"
        onChange={handleFileChange}
      />
      {isWorkPage ? (
        <div
          onClick={() => {
            if (isLoading) {
              return;
            }
            inputRef?.current?.click();
          }}
          className={
            "flex cursor-pointer items-center text-base text-[#7B66F0]"
          }>
          {isLoading ? (
            <div className={classNames("text-purple h-5 w-5 animate-spin")}>
              <AiOutlineLoading3Quarters size={20} />
            </div>
          ) : (
            "Upload file"
          )}
        </div>
      ) : isEmailPage ? (
        <CgAttachment onClick={() => inputRef?.current?.click()} />
      ) : button ? (
        <div
          onClick={() => {
            if (isLoading) {
              return;
            }
            inputRef?.current?.click();
          }}>
          {button}
        </div>
      ) : (
        <Button
          label={""}
          icon={<HiPlus />}
          size={"custom"}
          colorType={"outline"}
          extraClasses={"btn-sm px-2"}
          disabled={isLoading}
          isLoading={isLoading}
          onClick={() => inputRef?.current?.click()}
        />
      )}
    </div>
  );
};
